import React, { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { ArrowLeft, CheckCircle2, AlertTriangle, Clock, User, MapPin, LogOut } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Active-visit screen shown after clocking in.
 * Renders the built-in ADLs/IADLs checklist, vitals inputs, notes, optional incident flag,
 * client signature capture, and a Clock Out button.
 */
export default function AideVisitView({ visit, onDone, onCancel }) {
  const [template, setTemplate] = useState(null);
  const [checked, setChecked] = useState({}); // { taskId: true }
  const [vitals, setVitals] = useState({});
  const [notes, setNotes] = useState('');
  const [incidentReported, setIncidentReported] = useState(false);
  const [incidentDetails, setIncidentDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState(visit?.patient_name || '');
  const [elapsed, setElapsed] = useState('');

  useEffect(() => {
    api.get('/care-logs/template').then(r => setTemplate(r.data)).catch(() => setTemplate({ adls: [], iadls: [], vitals: [] }));
  }, []);

  // Elapsed time counter
  useEffect(() => {
    const tick = () => {
      if (!visit.clock_in_at) return;
      const diff = Math.floor((Date.now() - new Date(visit.clock_in_at).getTime()) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [visit.clock_in_at]);

  const toggleTask = (id) => setChecked(prev => ({ ...prev, [id]: !prev[id] }));

  const getLocation = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });

  const submitClockOut = async (sigDataUrl) => {
    setSubmitting(true);
    try {
      const location = await getLocation();
      const tasksCompleted = Object.keys(checked).filter(k => checked[k]);
      const body = {
        location,
        tasks_completed: tasksCompleted,
        vitals,
        general_notes: notes,
        incident_reported: incidentReported,
        incident_details: incidentReported ? incidentDetails : null,
        signature_data_url: sigDataUrl || null,
        signer_name: sigDataUrl ? (signerName || visit.patient_name) : null,
        device_info: navigator.userAgent,
      };
      await api.post(`/care-logs/assignments/${visit.id}/clock-out`, body);
      toast.success('Clock out successful. Visit complete.');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-out failed');
    } finally {
      setSubmitting(false);
      setShowSignModal(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24" data-testid="aide-visit-view">
      {/* Header */}
      <div className="sticky top-0 -mx-4 px-4 py-3 bg-white border-b border-[#E2E8F0] z-20">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} className="h-9" data-testid="back-to-visits-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[#0A2342] truncate flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{visit.patient_name}</div>
            <div className="text-xs text-[#92400E] flex items-center gap-1"><Clock className="w-3 h-3" />Active · {elapsed}</div>
          </div>
        </div>
      </div>

      {visit.patient_address && (
        <div className="text-xs text-[#64748B] flex items-center gap-1.5 px-1"><MapPin className="w-3 h-3" />{visit.patient_address}</div>
      )}

      {/* ADLs */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Activities of Daily Living (ADL)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(template?.adls || []).map(t => (
            <label key={t.id} className="flex items-center gap-3 py-2.5 border-b border-[#F1F5F9] last:border-b-0 cursor-pointer" data-testid={`adl-row-${t.id}`}>
              <Checkbox checked={!!checked[t.id]} onCheckedChange={() => toggleTask(t.id)} className="w-5 h-5" data-testid={`adl-check-${t.id}`} />
              <span className={`text-sm flex-1 ${checked[t.id] ? 'line-through text-[#94A3B8]' : 'text-[#0A2342]'}`}>{t.label}</span>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* IADLs */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Instrumental ADL (IADL)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(template?.iadls || []).map(t => (
            <label key={t.id} className="flex items-center gap-3 py-2.5 border-b border-[#F1F5F9] last:border-b-0 cursor-pointer" data-testid={`iadl-row-${t.id}`}>
              <Checkbox checked={!!checked[t.id]} onCheckedChange={() => toggleTask(t.id)} className="w-5 h-5" data-testid={`iadl-check-${t.id}`} />
              <span className={`text-sm flex-1 ${checked[t.id] ? 'line-through text-[#94A3B8]' : 'text-[#0A2342]'}`}>{t.label}</span>
            </label>
          ))}
        </CardContent>
      </Card>

      {/* Vitals */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Vitals (optional)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-3">
          {(template?.vitals || []).map(v => (
            <div key={v.id}>
              <label className="text-xs text-[#64748B] block mb-1">{v.label}</label>
              <Input
                value={vitals[v.id] || ''}
                onChange={(e) => setVitals(prev => ({ ...prev, [v.id]: e.target.value }))}
                placeholder={v.placeholder}
                className="careiq-input h-11 text-base"
                inputMode="decimal"
                data-testid={`vital-${v.id}`}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Visit Notes</CardTitle></CardHeader>
        <CardContent>
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observations, patient mood, any changes since last visit…"
            rows={4}
            className="careiq-input text-base"
            data-testid="visit-notes-input"
          />
        </CardContent>
      </Card>

      {/* Incident */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#F59E0B]" />Incident Reporting</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <Checkbox checked={incidentReported} onCheckedChange={(v) => setIncidentReported(!!v)} className="w-5 h-5" data-testid="incident-toggle" />
            <span className="text-sm text-[#0A2342]">I need to report an incident during this visit</span>
          </label>
          {incidentReported && (
            <Textarea
              value={incidentDetails}
              onChange={(e) => setIncidentDetails(e.target.value)}
              placeholder="Describe what happened, when, who was present…"
              rows={4}
              className="careiq-input text-base border-[#F59E0B]"
              data-testid="incident-details-input"
            />
          )}
        </CardContent>
      </Card>

      {/* Client signature */}
      <Card className="careiq-card">
        <CardHeader className="pb-2"><CardTitle className="text-base">Client / Caregiver Signature (optional)</CardTitle></CardHeader>
        <CardContent>
          {signature ? (
            <div className="space-y-2">
              <img src={signature} alt="Signature" className="border border-[#E2E8F0] rounded bg-white max-h-24" />
              <Button variant="outline" size="sm" onClick={() => setSignature(null)} data-testid="clear-sig-btn">Clear Signature</Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setShowSignModal(true)} className="w-full h-12" data-testid="capture-sig-btn">
              Tap to capture signature
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] p-3 z-30">
        <div className="max-w-3xl mx-auto">
          <Button
            onClick={() => submitClockOut(signature)}
            disabled={submitting}
            className="w-full bg-[#166534] hover:bg-[#14532D] text-white h-14 text-base"
            data-testid="clock-out-btn"
          >
            {submitting ? 'Saving…' : (<><LogOut className="w-5 h-5 mr-2" />Clock Out & Submit Log</>)}
          </Button>
        </div>
      </div>

      {showSignModal && (
        <SignatureModal
          onSave={(dataUrl) => { setSignature(dataUrl); setShowSignModal(false); }}
          onClose={() => setShowSignModal(false)}
        />
      )}
    </div>
  );
}

function SignatureModal({ onSave, onClose }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    ctx.strokeStyle = '#0A2342';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
  }, []);

  const getXY = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches?.[0] || e.changedTouches?.[0];
    const x = (touch?.clientX ?? e.clientX) - rect.left;
    const y = (touch?.clientY ?? e.clientY) - rect.top;
    return { x: x * (canvasRef.current.width / rect.width), y: y * (canvasRef.current.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    setDrawing(true);
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing) return;
    e.preventDefault();
    const { x, y } = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const end = () => setDrawing(false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) { toast.error('Please sign before saving'); return; }
    onSave(canvasRef.current.toDataURL('image/png'));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E2E8F0]">
          <h3 className="font-semibold text-[#0A2342]">Sign to confirm visit</h3>
          <p className="text-xs text-[#64748B]">Ask the client or caregiver to sign below.</p>
        </div>
        <div className="p-4">
          <canvas
            ref={canvasRef}
            width={600}
            height={240}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
            className="w-full border-2 border-dashed border-[#94A3B8] rounded-lg touch-none bg-white"
            data-testid="signature-canvas"
          />
        </div>
        <div className="flex gap-2 px-4 pb-4">
          <Button variant="outline" onClick={clear} data-testid="sig-clear-btn">Clear</Button>
          <Button variant="ghost" onClick={onClose} className="ml-auto">Cancel</Button>
          <Button onClick={save} className="bg-[#0E7490] hover:bg-[#0C6580] text-white" data-testid="sig-save-btn">Save</Button>
        </div>
      </div>
    </div>
  );
}
